/**
 * Puzzle Mode Matching Engine
 * 
 * Implements Dr. Mario-style line-clearing mechanics for Puzzle Mode.
 * Matches are formed by 3 or more cells of the same type in a row
 * (horizontal or vertical), rather than blob-based matching.
 * 
 * Key Differences from Puyo/Endless Mode:
 * - Line-based matching (3+ in a row) instead of blob matching (4+ connected)
 * - Cancer cells (viruses) are eliminated when matched cells are adjacent
 * - Capsule halves can break apart when one side is cleared
 * - Wildcards (stem cells) can complete lines
 */

import { BaseMatchingEngine, MatchResult } from '../core/BaseMatchingEngine'
import { Grid, MatchGroup } from '../Grid'
import { Cell, CellType } from '../CellTypes'
import { GameBalanceManager } from '../GameBalanceManager'

export class PuzzleMatchingEngine extends BaseMatchingEngine {
    constructor(grid: Grid, balanceManager?: GameBalanceManager) {
        super(grid, balanceManager)
    }

    /**
     * Process a single cascade step (Dr. Mario style).
     * - If matches exist: increment chain, score/clear, apply capsule-aware gravity, return hasMore
     * - If no matches: return hasMore=false
     */
    public processMatches(): MatchResult {
        // Find current line matches (3+ in a row horizontally or vertically)
        const matches = this.findLineMatches()

        if (matches.length === 0) {
            return {
                matches: [],
                clearedCancer: [],
                totalCellsCleared: 0,
                chainLevel: this.currentChainLevel,
                scoreGained: 0,
                hasMore: false,
            }
        }

        this.currentChainLevel += 1
        const chainResult = this.calculateChainScore(matches, this.currentChainLevel)

        // Mark cells for deletion
        for (const match of matches) {
            this.grid.markCellsForDeletion(match.cells)
        }

        // Clear adjacent cancer cells (viruses)
        const clearedCancer = this.clearAdjacentCancer(matches)

        // Break capsule connections for matched cells (Dr. Mario capsule splitting)
        // This must happen before removal so orphaned halves can fall independently
        for (const match of matches) {
            for (const cell of match.cells) {
                if (cell.capsulePairId) {
                    this.grid.breakCapsuleConnection(cell)
                }
            }
        }

        // Also break connections for cleared cancer cells
        for (const cancerCell of clearedCancer) {
            if (cancerCell.capsulePairId) {
                this.grid.breakCapsuleConnection(cancerCell)
            }
        }

        // Remove matched cells and apply capsule-aware gravity
        this.grid.removeMarkedCells()
        while (this.grid.applyGravityWithCapsules()) { /* settle */ }

        // Count unique cleared cells
        const uniqueClearedIds = new Set<string>()
        for (const match of matches) {
            for (const cell of match.cells) {
                uniqueClearedIds.add(cell.id)
            }
        }
        for (const cell of clearedCancer) {
            uniqueClearedIds.add(cell.id)
        }
        const totalCellsCleared = uniqueClearedIds.size

        // Check if more matches exist after gravity settles
        const moreMatchesPending = this.findLineMatches().length > 0

        return {
            matches,
            clearedCancer,
            totalCellsCleared,
            chainLevel: this.currentChainLevel,
            scoreGained: chainResult.score,
            hasMore: moreMatchesPending,
        }
    }

    /**
     * Find all line matches (3+ cells in a row, horizontal or vertical)
     */
    private findLineMatches(): MatchGroup[] {
        const matches: MatchGroup[] = []
        const processedH = new Set<string>()
        const processedV = new Set<string>()

        // Check horizontal lines
        for (let y = 0; y < Grid.HEIGHT; y++) {
            for (let x = 0; x < Grid.WIDTH; x++) {
                const cell = this.grid.getCell(x, y)
                if (!cell || processedH.has(cell.id)) continue

                const lineMatch = this.findHorizontalLine(x, y)
                if (lineMatch && lineMatch.size >= 3) {
                    matches.push(lineMatch)
                    lineMatch.cells.forEach(c => processedH.add(c.id))
                }
            }
        }

        // Check vertical lines (separate set so cells shared with
        // horizontal matches can still start vertical matches)
        for (let x = 0; x < Grid.WIDTH; x++) {
            for (let y = 0; y < Grid.HEIGHT; y++) {
                const cell = this.grid.getCell(x, y)
                if (!cell || processedV.has(cell.id)) continue

                const lineMatch = this.findVerticalLine(x, y)
                if (lineMatch && lineMatch.size >= 3) {
                    matches.push(lineMatch)
                    lineMatch.cells.forEach(c => processedV.add(c.id))
                }
            }
        }

        return matches
    }

    /**
     * Find a horizontal line of matching cells starting at (x, y)
     */
    private findHorizontalLine(x: number, y: number): MatchGroup | null {
        const startCell = this.grid.getCell(x, y)
        if (!startCell || !this.canMatchInLine(startCell.type)) return null

        const cells: Cell[] = [startCell]
        // Resolve the line type: if start is STEM, adopt the first non-STEM type
        let resolvedType = startCell.type

        // Extend to the right
        let currentX = x + 1
        while (currentX < Grid.WIDTH) {
            const cell = this.grid.getCell(currentX, y)
            if (!cell || cell.type === CellType.CANCER) break

            // If we haven't resolved a concrete type yet, adopt the first non-STEM type
            if (resolvedType === CellType.STEM && cell.type !== CellType.STEM) {
                resolvedType = cell.type
            }

            if (!this.cellMatchesType(cell, resolvedType)) break
            cells.push(cell)
            currentX++
        }

        return cells.length >= 3 ? { cells, type: resolvedType, size: cells.length } : null
    }

    /**
     * Find a vertical line of matching cells starting at (x, y)
     */
    private findVerticalLine(x: number, y: number): MatchGroup | null {
        const startCell = this.grid.getCell(x, y)
        if (!startCell || !this.canMatchInLine(startCell.type)) return null

        const cells: Cell[] = [startCell]
        // Resolve the line type: if start is STEM, adopt the first non-STEM type
        let resolvedType = startCell.type

        // Extend downward
        let currentY = y + 1
        while (currentY < Grid.HEIGHT) {
            const cell = this.grid.getCell(x, currentY)
            if (!cell || cell.type === CellType.CANCER) break

            // If we haven't resolved a concrete type yet, adopt the first non-STEM type
            if (resolvedType === CellType.STEM && cell.type !== CellType.STEM) {
                resolvedType = cell.type
            }

            if (!this.cellMatchesType(cell, resolvedType)) break
            cells.push(cell)
            currentY++
        }

        return cells.length >= 3 ? { cells, type: resolvedType, size: cells.length } : null
    }

    /**
     * Check if a cell type can participate in line matches
     * Cancer cells cannot be matched directly, only cleared when adjacent
     */
    private canMatchInLine(type: CellType): boolean {
        // Cancer cells cannot be matched - they can only be cleared when adjacent to matches
        return type !== CellType.CANCER
    }

    /**
     * Check if a cell matches a given type (wildcards match any type)
     */
    private cellMatchesType(cell: Cell, type: CellType): boolean {
        // Cancer cells can never match anything
        if (cell.type === CellType.CANCER) {
            return false
        }

        // Stem cells are wildcards and match any type
        if (cell.type === CellType.STEM || type === CellType.STEM) {
            return true
        }
        return cell.type === type
    }

    /**
     * Clear cancer cells adjacent to matched cells
     */
    private clearAdjacentCancer(matches: MatchGroup[]): Cell[] {
        const clearedCancer: Cell[] = []
        const processedCancer = new Set<string>()

        for (const match of matches) {
            for (const cell of match.cells) {
                // Check all four adjacent positions
                const neighbors = [
                    { x: cell.x - 1, y: cell.y }, // left
                    { x: cell.x + 1, y: cell.y }, // right
                    { x: cell.x, y: cell.y - 1 }, // up
                    { x: cell.x, y: cell.y + 1 }  // down
                ]

                for (const pos of neighbors) {
                    const neighbor = this.grid.getCell(pos.x, pos.y)
                    if (neighbor && this.isCancerCell(neighbor) && !processedCancer.has(neighbor.id)) {
                        this.grid.markCellsForDeletion([neighbor])
                        clearedCancer.push(neighbor)
                        processedCancer.add(neighbor.id)
                    }
                }
            }
        }

        return clearedCancer
    }

    /**
     * Check if a cell is a cancer cell (virus)
     */
    private isCancerCell(cell: Cell): boolean {
        return cell.type === CellType.CANCER
    }

    /**
     * Calculate score for matches at a chain level
     */
    protected calculateChainScore(
        matches: MatchGroup[],
        chainLevel: number
    ): { score: number } {
        let score = 0
        const multiplier = this.getChainMultiplier(chainLevel)

        for (const match of matches) {
            // Base score: 10 points per cell
            const baseScore = match.size * 10

            // Length bonus for longer lines
            let lengthBonus = 0
            if (match.size >= 4) lengthBonus = (match.size - 3) * 20
            if (match.size >= 6) lengthBonus += (match.size - 5) * 40

            const matchScore = Math.floor((baseScore + lengthBonus) * multiplier)
            score += matchScore
        }

        return { score }
    }

    /**
     * Check for immediate matches without processing them
     */
    public hasMatches(): boolean {
        const matches = this.findLineMatches()
        return matches.length > 0
    }

    /**
     * Get potential matches for preview/hint system
     */
    public getPotentialMatches(): MatchGroup[] {
        return this.findLineMatches()
    }
}
